import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FilterCriteria } from "tasks-experience-client";
import { IdValuePairDTO, WorkflowState } from "common-util-experience-client";
import { GridDropdown } from "../../grid/GridDropdown";
import { createClinicalAlertsApi, createPartnerApi } from "../../api-helpers/PatientApiHelper";
import { CommandCenterConstants, Constants } from "../../constants/Lookups";
import { UsersDto } from "users-experience-client";
import { GridUsersDropdown } from "../../grid/GridUsersDropdown";
import { IdNamePair, Partner } from "patients-experience-client";
import { NavDropdown } from "react-bootstrap";
import { Datepicker } from "@mobiscroll/react";
import { createUsersApi } from "../../api-helpers/UserApiHelper";
import OffCanvas from "../../components/offCanvas";
import { AddEditAlert } from "./AddEditAlert";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";

interface CommandCenterHeaderProps {
  patientId?: string;
  onUpdateFilters: (filterArray: FilterCriteria[]) => void;
  reloadGrid: () => void;
  breadcrumbs?: boolean;
}

export const CommandCenterHeader = (props: CommandCenterHeaderProps) => {
  const [searchText, setSearchText] = useState<string>();
  const [isDefaultUserLoaded, setIsDefaultUserLoaded] = useState(false);
  const [partners, setPartners] = useState<IdValuePairDTO[] | null>();
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [episodeStatuses, setEpisodeStatuses] = useState<IdValuePairDTO[] | null>();
  const [initialStatusKeys, setInitialStatusKeys] = useState<string[]>([]);
  const [alertCategories, setAlertCategories] = useState<IdValuePairDTO[] | null>();
  const [alerts, setAlerts] = useState<IdValuePairDTO[] | null>();
  const [episodeTypes, setEpisodeTypes] = useState<IdValuePairDTO[] | null>();
  const [assignedTo, setAssignedTo] = useState<IdValuePairDTO[] | null>();
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);
  const [tempDateRange, setTempDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });

  const clinicalAlertsApi = createClinicalAlertsApi();
  const usersApi = createUsersApi();
  const partnerApi = createPartnerApi();
  const statusCache = useRef<IdValuePairDTO[] | null>(null);

  const getEpisodeTypes = async () => {
    try {
      const { data } = await clinicalAlertsApi.clinicalAlertsControllerGetAllEpisodeTypes();
      const mappedResponse = data.map((item: IdNamePair) => ({
        id: item.id,
        value: item.name,
      }));
      return mappedResponse;
    } catch (error) {
      console.error('Failed to fetch episode types');
      return [];
    }
  };

  const getEpisodeStatuses = async () => {
    if (statusCache.current) return statusCache.current;

    try {
      const { data } = await clinicalAlertsApi.clinicalAlertsControllerGetAllEpisodeStatuses();
      const mappedResponse = data.map((item: IdNamePair) => ({
        id: item.id,
        value: `${item.rollup} (${item.name})`,
      }));
      statusCache.current = mappedResponse;
      return mappedResponse;
    } catch (error) {
      console.error('Failed to fetch episode statuses');
      return [];
    }
  };

  const getPartners = async () => {
    try {
      const { data } = await partnerApi.partnerControllerGetAllPartners(true);
      const mappedResponse = data.map((item: Partner) => ({
        id: item.partnerId,
        value: item.name,
      }));
      return mappedResponse;
    } catch (error) {
      console.error('Failed to fetch partners');
      return [];
    }
  };

  const getAlertCategories = async () => {
    try {
      const { data } = await clinicalAlertsApi.clinicalAlertsControllerGetAllEpisodeCategories();
      const mappedResponse = data.map((item: IdNamePair) => ({
        id: item.id,
        value: item.name,
      }));
      return mappedResponse;
    } catch (error) {
      console.error('Failed to fetch alert categories');
      return [];
    }
  };

  const getAlerts = async () => {
    try {
      const { data } = await clinicalAlertsApi.clinicalAlertsControllerGetAllEventTypes();
      const mappedResponse = data.map((item: IdNamePair) => ({
        id: item.id,
        value: item.name,
      }));
      return mappedResponse;
    } catch (error) {
      console.error('Failed to fetch alerts');
      return [];
    }
  };

  const handleDateChange = (event: any) => {
    const value: [Date | null, Date | null] = event.value;
    setTempDateRange(value);
    const [start, end] = value;

    if (start && end) {
      const startDate = new Date(start);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(end);
      endDate.setHours(0, 0, 0, 0);

      setDateRange([startDate, endDate]); // actual filter state
    }
  };

  const updateAssignedTo = (values: UsersDto[]) => {
    setAssignedTo(values ? values.map(v => {
      return { id: v.id, value: `${v.firstName} ${v.lastName}` };
    }) : null);
  }

  const handleAddClinicalAlert = () => {
    setOffCanvasShow(true);
  };

  const handleShowToaster = async (toasterConfig: ToastConfig) => {
    setToastConfig(toasterConfig as ToastConfig);
    setShowToast(true);
  };

  const filters: FilterCriteria[] = [];
  const updateFilters = () => {
    if (props.patientId) {
      filters.push({
        propertyName: "patientId",
        operator: "equals",
        propertyValue: props.patientId
      });
      if (searchText?.length) {
        filters.push({
          propertyName: "eventTypeName",
          operator: "contains",
          propertyValue: searchText
        });
      }
    }
    if (props.patientId === undefined) {
      const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
      if (searchText && datePattern.test(searchText)) {
        filters.push({
          propertyName: "patientDateOfBirth",
          operator: "equals",
          propertyValue: searchText,
        });
      } else if (searchText?.length) {
        filters.push({
          propertyName: "patientFullName",
          operator: "contains",
          propertyValue: searchText
        });
        filters.push({
          propertyName: "patientWellinksId",
          operator: "contains",
          propertyValue: searchText
        });
      }
    }
    if (assignedTo && assignedTo.length > 0) {
      filters.push({
        propertyName: "alertAssigneeId",
        operator: "in",
        propertyValue: assignedTo.map((a) => a.id),
      });
    }
    if (partners && partners.length > 0) {
      filters.push({
        propertyName: "partnerId",
        operator: "in",
        propertyValue: partners.map((p) => p.id),
      });
    }
    if (episodeStatuses && episodeStatuses.length > 0) {
      filters.push({
        propertyName: "episodeStatusId",
        operator: "in",
        propertyValue: episodeStatuses.map((e) => e.id),
      });
    }
    if (episodeTypes && episodeTypes.length > 0) {
      filters.push({
        propertyName: "episodeTypeId",
        operator: "in",
        propertyValue: episodeTypes.map((e) => e.id),
      });
    }
    if (alerts && alerts.length > 0) {
      filters.push({
        propertyName: "eventTypeId",
        operator: "in",
        propertyValue: alerts.map((e) => e.id),
      });
    }
    if (alertCategories && alertCategories.length > 0) {
      filters.push({
        propertyName: "episodeCategoryId",
        operator: "in",
        propertyValue: alertCategories.map((e) => e.id),
      });
    }
    if (dateRange && dateRange.length === 2) {
      filters.push({
        propertyName: "episodeCreatedAt",
        operator: "greater-than-equals",
        propertyValue: dateRange[0],
      });
      filters.push({
        propertyName: "episodeCreatedAt",
        operator: "less-than-equals",
        propertyValue: dateRange[1],
      });
    }

    props.onUpdateFilters(filters);
  };

  useEffect(() => {
    const fetchCommandCenterUser = async () => {
      const commandCenterUser = await usersApi.usersControllerSearchUsers(undefined, CommandCenterConstants.COMMAND_CENTER);
      if (commandCenterUser.data.length > 0) {
        commandCenterUser.data.map((user: any) => {
          setAssignedTo([{
            id: user.key,
            value: user.value,
          }]);
        });
        setIsDefaultUserLoaded(true);
      }
    };
    const fetchEpisodeStatuses = async () => {
      const allStatuses = await getEpisodeStatuses();
      const defaults = allStatuses.filter((s: IdValuePairDTO) => !s.value.startsWith("Closed"));
      setInitialStatusKeys(defaults.map((s: IdValuePairDTO) => s.id));
      setEpisodeStatuses(defaults);
    };

    fetchCommandCenterUser();
    fetchEpisodeStatuses();
  }, []);

  useEffect(() => {
    if (isDefaultUserLoaded) {
      updateFilters();
    }
  }, [searchText, assignedTo, partners, episodeStatuses, episodeTypes, alerts, alertCategories, dateRange]);

  return (
    <>
      <div className="row">
        <Toaster
          show={showToast}
          onClose={() => setShowToast(false)}
          type={toastConfig.type}
          title={toastConfig.title}
          message={toastConfig.message}
          icon={toastConfig.icon}
          addClass={'mt-0 py-1 min-w-22-5'}
        />
      </div>
      {props.breadcrumbs && (
        <div
          className="d-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom"
          aria-label="breadcrumb"
        >
          <div className="flex-fill d-flex align-items-center">
            <div>
              <ol className="breadcrumb">
                <li className="breadcrumb-item breadcrumb-item-first">
                  <Link to="/">
                    <span className="material-symbols-outlined fs-14">
                      home
                    </span>
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Command Center
                </li>
              </ol>
              <h1 className="h3 m-0">Command Center</h1>
            </div>
          </div>
          <div className="justify-content-end d-flex align-items-center">
            <button
              title="Add patient alert"
              className="btn btn-sm btn-outline-secondary ms-3 p-1 rounded-2"
              onClick={() => handleAddClinicalAlert()}
            >
              <span className="material-symbols-outlined fs-28 align-text-top">
                notifications
              </span>
            </button>
          </div>
        </div>
      )}

      <div className="d-md-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom">
        <div className="flex-fill d-flex align-items-center">
          <div className="search-box max-w-md-18-75">
            <span className="material-symbols-outlined search-box__icon">
              search
            </span>
            <div className="typeahead-container search-box d-flex">
              <span className="material-symbols-outlined search-box__icon">
                search
              </span>
              <input
                type="text"
                name="searchPatient"
                className="form-control search-box__input fw-normal fs-12"
                placeholder={props.patientId ? "Search by alert" : "Search by Name, Wellinks ID, or DoB"}
                onChange={(event) => setSearchText(event.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center">

          {props.patientId === undefined && (
            <>
              <GridDropdown
                label="Type"
                keyProvider={(item) => item.id}
                labelProvider={(item) => item.value}
                dataProvider={getEpisodeTypes}
                onChange={setEpisodeTypes}
              />
              <GridDropdown
                label="Alert"
                keyProvider={(item) => item.id}
                labelProvider={(item) => item.value}
                dataProvider={getAlerts}
                onChange={setAlerts}
              />
              <GridDropdown
                label="Alert Category"
                keyProvider={(item) => item.id}
                labelProvider={(item) => item.value}
                dataProvider={getAlertCategories}
                onChange={setAlertCategories}
              />
              <NavDropdown
                id="nav-dropdown-dark-example"
                title="Created On"
                className={`btn-dropdown ms-md-2 me-2 me-md-0 mt-2 mt-md-0 ${dateRange?.length ? 'border border-primary rounded-2' : 'border border-white'}`}
              >
                <div style={{ width: "500px" }}>
                  <Datepicker
                    controls={["calendar"]}
                    select="range"
                    calendarType="month"
                    pages={2}
                    touchUi={true}
                    display="inline"
                    value={tempDateRange}
                    onChange={handleDateChange}
                    theme="windows"
                    themeVariant="light"
                  />
                </div>
              </NavDropdown>
            </>
          )}


          <GridDropdown
            label="Status"
            keyProvider={(item) => item.id}
            labelProvider={(item) => item.value}
            dataProvider={getEpisodeStatuses}
            initialKeys={initialStatusKeys}
            onChange={setEpisodeStatuses}
          />

          {props.patientId === undefined ? (<GridDropdown
            label="Partnership"
            keyProvider={(item) => item.id}
            labelProvider={(item) => item.value}
            dataProvider={getPartners}
            onChange={setPartners}
          />) : null}

          <GridUsersDropdown
            label="Assignee"
            onChange={updateAssignedTo}
            initialUserIds={assignedTo?.map((data) => data.id) ?? []}
          />
        </div>
      </div>
      <OffCanvas
        title={"Add Alert"}
        offCanvasShow={offCanvasShow}
        setOffCanvasShow={setOffCanvasShow}
      >
        <AddEditAlert
          setOffCanvasShow={setOffCanvasShow}
          handleShowToaster={handleShowToaster}
        />
      </OffCanvas>
    </>
  );
};
